/**
 * Additional numerology course lessons (merged into NUMEROLOGY_ARTICLES).
 */

const ctaNote =
  'Each lesson teaches method and general meaning. A generated report applies the same rules to your birth date and name, then weaves them into a written synthesis.';

const ctaCourse =
  'This lesson is part of the structured course. It teaches method and interpretation — separate from the optional personal report product.';

export const NUMEROLOGY_COURSE_EXTRA_ARTICLES = {
  'm1-how-numerology-works': {
    title: 'How numerology works',
    section: 'about',
    seo: ['How numerology works'],
    intro:
      'Most work begins with a birth date and a birth name. Numbers are produced with one letter table and consistent reduction — then read in positions, not as isolated labels.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'You are learning one pipeline: **inputs** (legal birth date + birth name spelling) → **mechanics** (map letters to digits, sum and reduce by position rules) → **positions** (Life Path, name numbers, birthday, timing, optional secondaries) → **interpretation** (same digit, different job per office).',
        ],
      },
      {
        h2: 'What a chart actually contains',
        p: [
          'From the date you derive long-arc material (Life Path, Birthday number, cycles). From the name you derive Expression (all letters), Soul Urge (vowels), and Personality (consonants), using one letter-to-number table and consistent reduction.',
          'Master numbers 11, 22, and 33 are not sprinkled everywhere they appear in scratch work. They are kept only when the rules for that final step say so — otherwise you reduce through them. That keeps “master” meaningful instead of decorative.',
        ],
      },
      {
        h2: 'Letters and the Pythagorean table',
        p: [
          'Letters A through Z map to 1–9 in repeating cycles (A–I, J–R, S–Z). Spaces and punctuation do not count. Expression uses every letter in the full birth name. Soul Urge and Personality split vowels and consonants so the same name yields three coherent angles: outer style, inner appetite, and the whole instrument.',
          'Spell Y consistently so hand calculation can match software — inconsistency there is the usual reason totals disagree.',
        ],
      },
      {
        h2: 'Reduction — step by step',
        ol: [
          'Sum only the digits allowed for that position (month only, vowels only, full name, etc.).',
          'If the sum is more than one digit, add those digits again unless your rules say this total may stand as 11, 22, or 33 for this **final** step.',
          'Repeat until you reach 1–9 or an allowed master. Write each intermediate line on paper so errors are visible.',
        ],
      },
      {
        h2: 'Worked example — letters only',
        p: [
          'Name part “ANN” → A=1, N=5, N=5 → sum 11. If this sum is a Soul Urge subtotal, your engine either keeps 11 or reduces to 2 — state the rule you used. Name part “JO” → J=1, O=6 → 7.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'No single output replaces the chart. Life Path answers pacing; Expression answers how you tend to work the world; timing answers “what season is this?” Always cite which position a digit belongs to before you interpret.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Mixing Chaldean and Pythagorean maps in one chart.',
          'Treating every 11 in scratch work as a master Life Path.',
          'Forgetting to reduce the birth year before adding to month and day for Life Path.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Write the Pythagorean values for your own first name letters only. Sum and reduce. Then write one sentence: this number is **not** yet a chart position — it is drill work — explain why.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Open **Core numbers overview**, then **Reduction rules** for more arithmetic drills before you calculate full positions.',
        ],
      },
    ],
    howInReport: 'The engine applies the same table and reduction rules you use on paper.',
    ctaNote,
  },

  'm1-reduction-rules': {
    title: 'Reduction rules',
    section: 'about',
    seo: ['Numerology reduction'],
    intro:
      'Until a sum is a single digit, add its digits and repeat. Stop at 1–9, or at 11, 22, or 33 when that position’s rules allow that total to stand as the final result.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Reduction turns large sums into interpretable digits. The **starting pool** changes by position (month only, vowels only, full name, etc.), but the **closing habit** is shared: add digits, repeat, respect masters only when that position’s **final** step allows them.',
        ],
      },
      {
        h2: 'Calculation — the habit',
        ol: [
          'Sum only the digits that belong to this step (never mix positions).',
          'If the sum is 11, 22, or 33, check: is this the **final** result for this position? If yes and rules allow, stop. If no, reduce further (e.g. 11 → 2).',
          'For any other two-or-more-digit total, add digits and repeat until one digit remains or an allowed master appears at the allowed step.',
        ],
        ul: [
          'Life Path: reduce month, day, and year separately first; combine; then final reduction (masters only if the final step says so).',
          'Name numbers: map letters → sum the group you need → reduce the same way.',
        ],
      },
      {
        h2: 'Worked examples',
        p: [
          '38 → 3 + 8 = 11. If 11 is allowed as the final Life Path total for that calculation, it stays; if not, 1 + 1 → 2.',
          '34 → 3 + 4 = 7. Ordinary closure.',
          '29 → 2 + 9 = 11 — same “eleven” shape as 38, different path; interpretive weight still depends on position and whether 11 may stand.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Stopping at 11 when the position requires a final single digit.',
          'Reducing masters in an intermediate line that should have been carried forward before the last step.',
          'Skipping pencil rows — transposed digits in birth years hide in “mysterious” meaning debates.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Reduce these totals on paper and state whether 11 could stand as a **final** Soul Urge (yes/no depends on your engine — pick one rule and stay consistent): 47, 38, 20, 33.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Apply reduction inside **Life Path** and each **name** lesson next; keep the same habit everywhere.',
        ],
      },
    ],
    howInReport: 'The engine uses the same reduction checkpoints as hand calculation.',
    ctaNote,
  },

  'm1-structure-of-a-reading': {
    title: 'How a numerology reading is structured',
    section: 'about',
    seo: ['Numerology reading structure'],
    intro:
      'A full reading moves from terrain to weather: core positions first, then timing, then synthesis — not from a single “lucky number.”',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'A reading is an **ordered stack**: you establish what the chart **is** (core numbers), then what **season** it is in (timing), then how those layers **negotiate** (synthesis). Jumping to timing first usually confuses the client and hides arithmetic errors in the core.',
        ],
      },
      {
        h2: 'A sensible order',
        ol: [
          'Life Path — one paragraph on long arc.',
          'Name cluster — Expression, Soul Urge, Personality as one conversation; note agreement or tension.',
          'Birthday — close-up texture.',
          'Secondary numbers (Module 4) only if calculated and clarifying.',
          'Timing — Personal Year, then Month/Day if useful.',
          'Synthesis — two short paragraphs: dominant themes + one reflective question.',
        ],
      },
      {
        h2: 'Worked mini-outline',
        p: [
          'Sample spine: “Terrain: 6 Life Path — care and responsibility recur. Name: Expression 3 / Soul Urge 7 / Personality 5 — visible creativity, private depth, mobile surface. Birthday adds [digit]. Weather: Personal Year 4 — consolidation season.” Then one sentence on how 4’s structure meets 3’s visibility.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Listing every number with equal weight.',
          'Leading with Personal Year before stating Life Path.',
          'Treating secondary markers as headlines when the core is still unverified.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Without calculating, outline a reading for a fictional client in **six bullet headings** from the list above. Under each heading, write one question you will answer — not the answer yet.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Module 2 teaches **how to compute** each core position; later modules add secondaries and timing so this outline has real numbers to fill in.',
        ],
      },
      {
        h2: 'Course vs. optional report',
        p: [
          'These lessons teach method in the abstract. The optional personal report is a separate product: it applies the same rules to your dated, named chart in one written document. The course does not require you to purchase a report.',
        ],
      },
    ],
    howInReport:
      'If you use the report product, it follows this architecture: synthesis across positions with timing as context.',
    ctaNote: ctaCourse,
  },

  'm2-master-life-paths': {
    title: 'Master Life Paths 11, 22, 33',
    section: 'chart',
    seo: ['Master life path'],
    intro:
      'When the final Life Path total resolves to 11, 22, or 33, the theme widens: more sensitivity, more scale, more obligation to integration. The adjacent single digit still matters as the floor under the master.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'On the Life Path only, a master means the **final** sum after month+day+year work is allowed to stay 11, 22, or 33. The adjacent single (1, 4, 6) is the “floor” theme when voltage feels high; the master names wider stakes and finer nerves along that same line.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Eleven leans on intuition and nerve; twenty-two on building something durable from vision; thirty-three on compassion with accountability.',
          'Review the **Master numbers** lesson for mechanics; here the emphasis is the **Life Path arc** specifically — decades-long pacing, not a single mood.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'A master Life Path does not erase friction elsewhere. Expression, Soul Urge, cycles, and birthday texture still qualify the story. Name numbers tell **how** the person carries the master in public and in private.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Declaring “master soul” because 11 appeared while reducing a name subtotal — wrong step, wrong position.',
          'Using master language to avoid naming concrete behaviours.',
          'Ignoring the single-digit floor — integration usually lives there.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Take a Life Path you know is 11. Write three sentences: (1) single-digit floor theme, (2) how master widens it, (3) one boundary that protects nerves. Repeat mentally for 22 and 33 with your own wording.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Continue with **Expression**, **Soul Urge**, and **Personality** — masters can appear there too, but the same final-step rule applies.',
        ],
      },
    ],
    howInReport: 'A master Life Path is written as part of the full portrait, not as an isolated halo.',
    ctaNote,
  },

  'm2-expression-number': {
    title: 'Expression number',
    section: 'chart',
    seo: ['Expression number'],
    intro:
      'Expression uses every letter of the birth name. It describes the fullest picture of how the name moves through the world: work, speech, signature style — the “instrument” you play in public.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Expression answers: “How does this person tend to **do** life in the world — skill, style, competence, signature?” It uses **every** letter of the birth name, so it is broader than Soul Urge or Personality alone.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Write the full birth name; strip to letters A–Z only (no spaces or punctuation).',
          'Map each letter to its Pythagorean value (A–I = 1–9, J–R, S–Z repeat).',
          'Sum all values. Reduce by adding digits until you reach 1–9, or stop at 11/22/33 only if that final total is allowed for Expression in your system.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          '“ANN LEE” → ANN = 1+5+5 = 11; LEE = 3+5+5 = 13 → 4; total 11 + 4 = 15 → 1 + 5 = 6 (or treat 11 + 4 without premature reduction per your engine — show your steps). Use a real name on your own paper next.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Borrow the 1–9 vocabulary from **Numbers 1–9**. Same digit as Life Path still differs: Life Path = long arc; Expression = instrument and performance style.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Pair with Soul Urge: when inner appetite and outer instrument diverge, the reading names honest split, not failure. Birthday and timing colour how loudly Expression shows this season.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Using a nickname instead of birth certificate spelling without saying so.',
          'Confusing Expression with Life Path “identity.”',
          'Dropping middle names inconsistently between lessons.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Expression for one full birth name on paper. Write two sentences: capability style + one friction if Soul Urge were hypothetically 2 and Expression were 8.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Soul Urge** isolates vowels; **Personality** isolates consonants — same name, different filters.',
        ],
      },
    ],
    howInReport: 'Expression appears in synthesis with Soul Urge and Personality, not as a lone label.',
    ctaNote,
  },

  'm2-soul-urge-number': {
    title: 'Soul Urge number',
    section: 'chart',
    seo: ['Soul Urge'],
    intro:
      'Soul Urge (Heart’s Desire) uses vowels only. It is appetite and private truth: what you reach toward when nobody is grading you.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Soul Urge maps **only vowels** (A, E, I, O, U) plus **Y** when your rule set counts Y as a vowel for that syllable. It is the private “want” beneath the public instrument of Expression.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Split the full birth name into parts (first, middle, last) as you do for Expression.',
          'In each part, list vowels A, E, I, O, U. Apply your consistent Y rule (here: Y acts as vowel when it carries vowel sound, ends a name part, or stands without another vowel in that part — match the engine).',
          'Map vowels to numbers, sum **all** vowel values across the full name, reduce to 1–9 or allowed master at the final step.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          '“JANE” → A, E = 1 + 5 = 6. “LYNN” → Y as vowel by rule = 7 + ? — work Y on paper per your rule; this is where hand totals usually diverge.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Read with 1–9 vocabulary as **inner appetite**: what refuels, what feels honest, what you reach for when unstaged.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Expression is the whole instrument; Soul Urge is what you want to play when the room is empty. Divergence between them is normal — name the split and the skill of bridging it.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Inconsistent Y handling between Soul Urge and Personality.',
          'Including consonants in the Soul Urge sum.',
          'Treating Soul Urge as “secret personality” instead of appetite and longing.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Soul Urge for one name; circle the letter that most risked a Y dispute. Write one sentence on how that vowel total would change public story if it were Expression.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Personality number** — consonants and the “first glance” lens.',
        ],
      },
    ],
    howInReport: 'Soul Urge is integrated with Expression and Personality in the name narrative.',
    ctaNote,
  },

  'm2-personality-number': {
    title: 'Personality number',
    section: 'chart',
    seo: ['Personality number'],
    intro:
      'Personality uses consonants — the “first glance” number. It is partial by design: one camera angle, not the whole person.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Personality answers: “What do people tend to **meet first** — pace, guard, surface style?” It sums **consonants** (letters not counted as vowels for Soul Urge), including Y when Y functions as a consonant in your rule set.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'From the full birth name, identify vowels the same way you did for Soul Urge.',
          'Every remaining letter is a consonant for this pass (including Y-as-consonant cases).',
          'Map to digits, sum, reduce to 1–9 or allowed master at the final step.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          '“ANN” — all consonants: N+N = 5+5 = 10 → 1. Contrast with vowel-only Soul Urge on the same part to see how thin the Personality slice can be on short names.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Describe first-contact habits: reserve, warmth, humour as shield, brisk pace, etc. This is not “fake” — it is partial information by design.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Triangulate with Expression and Soul Urge: surface (Personality), appetite (Soul Urge), full instrument (Expression). Timing can amplify or soften how much surface shows.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Treating Personality as the whole person.',
          'Using different Y rules than Soul Urge without noticing.',
          'Overwriting Personality with Life Path adjectives.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'For one name, write three adjectives from Personality only, three from Soul Urge only — no overlap words. Then one sentence on the tension or harmony between those lists.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Birthday number** and **timing** lessons add close-up and seasonal lenses on top of this core cluster.',
        ],
      },
    ],
    howInReport: 'Personality informs tone and surface pacing alongside Expression and Soul Urge.',
    ctaNote,
  },

  'm3-maturity-number': {
    title: 'Maturity number',
    section: 'chart',
    seo: ['Maturity number'],
    intro:
      'The Maturity number describes where the chart “wants to land” after early life — the tone of the second half when Life Path and Expression have had time to integrate. It is not a second Life Path; it is a synthesis digit.',
    blocks: [
      {
        h2: 'Calculation (Pythagorean method)',
        p: [
          'Reduce the Life Path to a single digit (or 11, 22, 33 if your system keeps masters for that position). Reduce the Expression the same way.',
          'Add the two results. Reduce the sum until you reach 1–9, or a master at the final step if your rules allow it. That final digit is the Maturity number.',
        ],
      },
      {
        h2: 'What it does in a reading',
        p: [
          'It answers: “What does this person grow into when the name and the long arc have learned to cooperate?” A 4 Life Path with a 7 Expression might mature toward a quieter, more specialised form of mastery than either digit alone suggests.',
        ],
      },
      {
        h2: 'Worked mini-example',
        p: [
          'Life Path 9, Expression 5 → 9 + 5 = 14 → 1 + 4 = 5. Maturity 5: movement and adaptation keep mattering, but with a calmer sense of what is worth changing.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use Maturity as a closing theme for life review — not as a replacement for Life Path or Expression.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Pick a public figure or fictional pair: assign hypothetical Life Path and Expression digits, compute Maturity on paper, write one sentence on “what they grow into.”',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Attitude number** narrows to month+day stance; **Hidden Passion** counts repeated letters.',
        ],
      },
    ],
    howInReport: 'If the engine computes maturity, it appears as a late-life synthesis line, not a headline.',
    ctaNote: ctaCourse,
  },

  'm3-attitude-number': {
    title: 'Attitude number',
    section: 'chart',
    seo: ['Attitude number'],
    intro:
      'The Attitude number (sometimes called Sun number) is a small lens on how you meet the world early in life — the default stance formed from the birth month and day before the full chart complicates the picture.',
    blocks: [
      {
        h2: 'Calculation',
        p: [
          'Reduce the birth month to a single digit (November = 11 → keep or reduce per your LP rules; many systems reduce month to 1–9 first). Reduce the birth day to a single digit (11, 22 as day stay as two-digit or reduce — match your Life Path rules).',
          'Add the month digit and day digit. Reduce again to 1–9 or, at the final step, 11, 22, 33 if allowed. That is the Attitude number.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'It colours first impressions and coping style under stress — not the whole personality. A 3 Attitude might joke before trusting; an 8 Attitude might tighten control when uncertain.',
        ],
      },
      {
        h2: 'How it differs from Personality',
        p: [
          'Personality (consonants) comes from the name. Attitude comes from the calendar date. They can disagree — that is useful: “how I meet stress” versus “how I sound.”',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use Attitude as a quick colour on top of core numbers — never as the sole verdict.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Attitude for your birth date. Write one line on “first move under stress” vs your Personality digit if you know it.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Hidden Passion** tracks repeated letters in the name.',
        ],
      },
    ],
    howInReport: 'Optional accent when the engine includes it.',
    ctaNote: ctaCourse,
  },

  'm3-hidden-passion': {
    title: 'Hidden Passion number',
    section: 'chart',
    seo: ['Hidden passion'],
    intro:
      'Hidden Passion highlights digits that appear **most often** in the letters of the birth name after you map A–Z to 1–9. It is “hidden” because it is easy to miss until you tally.',
    blocks: [
      {
        h2: 'Method',
        p: [
          'Write the full birth name in letters only. Map each letter to its Pythagorean value. Count how many times each digit 1–9 appears.',
          'The digit with the highest count is the Hidden Passion; ties can mean two passions or a blended theme.',
        ],
      },
      {
        h2: 'What a high count means',
        p: [
          'Repetition suggests a native lane: a chart with many 4s in the name may keep returning to structure even when the Life Path is restless. The digit is read with the same 1–9 vocabulary as elsewhere — but emphasis is “this keeps showing up.”',
        ],
      },
      {
        h2: 'Example',
        p: [
          'A name that yields 3, 3, 3, 6, 5 for its letter counts might show Passion 3 — communication and creativity as a recurring muscle, not a one-off.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use Hidden Passion to explain why someone feels “at home” in a theme that is not obvious from Life Path alone.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Tally digit frequencies for one full birth name. Circle the max count; write one non-mystical sentence on how that digit’s vocabulary shows as habit.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Karmic Debt** markers flag specific subtotals before final reduction — different question than passion counts.',
        ],
      },
    ],
    howInReport: 'May appear as emphasis when letter counts are pronounced.',
    ctaNote: ctaCourse,
  },

  'm3-karmic-debt': {
    title: 'Karmic Debt numbers',
    section: 'chart',
    seo: ['Karmic debt'],
    intro:
      'In many systems, **13, 14, 16, and 19** appear as subtotals before final reduction in Life Path or other date work. They are read as **intensified growth edges** — pressure to finish a skill, not a curse.',
    blocks: [
      {
        h2: 'The four markers',
        p: [
          '13/4 — work and discipline; the edge is quitting too early or grinding without strategy.',
          '14/5 — freedom with consequence; the edge is scattered freedom or escapism.',
          '16/7 — belief and fall; the edge is pride or isolation when truth shifts.',
          '19/1 — independence with integrity; the edge is doing it alone when help is needed.',
        ],
      },
      {
        h2: 'What “debt” means here',
        p: [
          'Language is practical: unfinished skill, recurring lesson, higher stakes — not moral punishment. Your job as a reader is to name the behaviour pattern, not to frighten.',
        ],
      },
      {
        h2: 'Where it shows up',
        p: [
          'Often when a partial sum before the final Life Path reduction lands on 13, 14, 16, or 19 — then reduces to 4, 5, 7, or 1. Confirm with your engine’s rules; some systems only flag certain positions.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'If a debt number appears, dedicate one clear sentence to it in the reading — what it asks the person to practice, not what they are “bad at.”',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Sketch a birth date where a subtotal **might** be 13, 14, 16, or 19 before final Life Path reduction (you can invent digits). Write one growth sentence for each marker without fear language.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Karmic Lessons** track **missing** digits in the name — complementary tool, not duplicate.',
        ],
      },
    ],
    howInReport: 'Surfaced only when the calculation path matches your engine’s rules.',
    ctaNote: ctaCourse,
  },

  'm3-karmic-lessons': {
    title: 'Karmic Lessons',
    section: 'chart',
    seo: ['Karmic lessons'],
    intro:
      'Karmic Lessons are **digits 1–9 missing** from the letters of the full birth name (after mapping letters to numbers). A missing digit suggests a theme that does not come automatically — it asks for conscious practice.',
    blocks: [
      {
        h2: 'How to find them',
        p: [
          'Map the birth name to digits. List which of 1–9 never appear. Each missing digit is a lesson channel — not a hole in “who you are.”',
        ],
      },
      {
        h2: 'How to interpret without panic',
        p: [
          'Pair each missing digit with the 1–9 vocabulary you already learned. Missing 2: partnership and nuance need attention. Missing 7: rest and discernment may be skipped until life insists.',
        ],
      },
      {
        h2: 'Relationship to karmic debt',
        p: [
          'Debt numbers often feel urgent; missing lessons feel like slow practice. One chart can have both — they answer different questions.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use missing digits to name one or two concrete habits to build — not a list of deficiencies.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Map your birth name letters to digits; list which of 1–9 never appear. Pick one missing digit and one weekly habit that practises its vocabulary.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Balance number** reads initials for stabilising moves under pressure.',
        ],
      },
    ],
    howInReport: 'Optional synthesis when missing digits stand out.',
    ctaNote: ctaCourse,
  },

  'm3-balance-number': {
    title: 'Balance number',
    section: 'chart',
    seo: ['Balance number'],
    intro:
      'The Balance number (sometimes called the Point of Balance) is derived from **the initials** of the birth name. It is a small lens on how you stabilise under pressure — the default move when you need to recover.',
    blocks: [
      {
        h2: 'Calculation',
        p: [
          'Take the first letter of each name part of the birth name (first, middle if any, last). Map each initial to its Pythagorean value.',
          'Sum those values and reduce to 1–9 or, at the final step, 11, 22, 33 if your rules allow.',
        ],
      },
      {
        h2: 'What it does in a reading',
        p: [
          'It answers: “When things wobble, what do I reach for first?” A Balance number 6 might reach for caretaking or repair; a 1 might reach for decisive action.',
        ],
      },
      {
        h2: 'Example',
        p: [
          'Initials A (1), J (1), M (4) → 1 + 1 + 4 = 6. Under stress, harmony and responsibility may be the first tool you grab — for good or for over-functioning.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use Balance as a coping-style note — secondary to Life Path and name cluster.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Balance from your initials. Write when that coping style helps — and when it over-functions.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Module 5 timing lessons (**Personal Year** onward) stack weather on the chart you have already built.',
        ],
      },
    ],
    howInReport: 'Light accent when initials are computed.',
    ctaNote: ctaCourse,
  },

  'm4-personal-year': {
    title: 'Personal Year number',
    section: 'forecasts',
    seo: ['Personal year'],
    intro:
      'Personal Year is derived from your birth month, birth day, and the calendar year in focus. It sets seasonal emphasis on top of your core chart — initiation, patience, creative risk, consolidation, change, care, analysis, power, or closure.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Personal Year answers: “What **season** is this calendar year for this birth date?” It is recomputed every January cycle using the same birth month and day plus the **new** year digits.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Reduce birth month to one digit (handle November per your Life Path rules).',
          'Reduce birth day to one digit (preserve or reduce 11/22 per your engine).',
          'Reduce the four digits of the calendar year in focus to one digit.',
          'Add the three reduced numbers. Reduce the sum to 1–9, or allow 11 or 22 at the final step if your system does.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          'Birth 14 July: month 7, day 14 → 5. Year 2026 → 2+0+2+6 = 10 → 1. Sum 7+5+1 = 13 → 4. Personal Year **4** — read as structure, consolidation, patience for that year on top of core chart.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Use the 1–9 cycle language lightly: 1 = initiation, 2 = cooperation, 3 = expression, 4 = work, 5 = change, 6 = care, 7 = analysis, 8 = power, 9 = closure. Keep tone practical, not fatalistic.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Name how the year’s digit meets Life Path: a 7 year on a 3 Life Path still has to speak, but may want editing and depth. Expression tells **how** the person will enact the year’s theme.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Forgetting to reduce the calendar year before adding.',
          'Using birth year instead of the year in focus.',
          'Letting Personal Year replace Life Path in the narrative.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute your own Personal Year for this year and next year. Write two sentences contrasting them as pacing advice, not fortune.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Personal Month** and **Personal Day** refine the same year into smaller windows; **Timing cycles** shows the full stack.',
        ],
      },
    ],
    howInReport: 'Personal Year appears as context for the year in view.',
    ctaNote,
  },

  'm4-personal-month': {
    title: 'Personal Month',
    section: 'forecasts',
    seo: ['Personal month'],
    intro:
      'Personal Month refines the Personal Year into chapter-sized pacing — preparation, action, integration — without losing the annual headline.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Personal Month answers: “Inside this **Personal Year**, what is this **calendar month** emphasising?” It is a shorter lens — useful for projects and pacing, not for rewriting identity.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Compute Personal Year for the calendar year that contains the month you are studying.',
          'Reduce the calendar month number (January = 1, October = 10 → 1, etc.).',
          'Add Personal Year + calendar month digit. Reduce to 1–9 or allowed master.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          'If Personal Year = 4 and you study October (month 10 → 1), 4 + 1 = 5. Month theme picks up movement and adjustment inside a year that still asks for structure — describe the negotiation, not two separate fortunes.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Sequence work: when to initiate, collaborate, revise, rest — **inside** the year’s headline. If Year and Month digits clash, that tension is the reading.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Core chart sets baseline needs; Month suggests how to pace them this month. Expression shows **style** of the push; Soul Urge shows what would feel honest.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Using month without computing Year first.',
          'Treating Month as independent of Year.',
          'Overloading clients with Month detail when Year is still unclear.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Personal Month for this month and next. For each, one bullet: “start,” “steady,” or “review” — tied to the digit’s practical meaning.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Personal Day** adds a light daily dial; skip it when it crowds the reading.',
        ],
      },
    ],
    howInReport: 'Finer timing appears where it helps your chart without overwhelming the long arc.',
    ctaNote,
  },

  'm4-personal-day': {
    title: 'Personal Day',
    section: 'forecasts',
    seo: ['Personal day'],
    intro:
      'Daily layers are subtle: small nudges inside the month’s story. Useful for pacing tasks, not for anxiety spirals.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Personal Day answers: “Inside this **Personal Month**, what subtle emphasis fits **this calendar day**?” It is the lightest layer — scheduling nuance, not identity.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Compute Personal Month for the calendar month and year of the day in question.',
          'Reduce the calendar day (e.g. 24 → 6).',
          'Add Personal Month + calendar day digit; reduce to 1–9 or allowed master.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          'Personal Month 5, calendar day 11 → 2: 5 + 2 = 7. A subtle nudge toward review, privacy, or analysis inside a month already leaning on change — keep language gentle.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Think of day energy as weather inside the month’s climate. If Month highlights communication, a Day might bias listening vs speaking — practical pacing only.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Using Day to frighten or predict events.',
          'Leading with Day before Year or core chart.',
          'Arguing over Day when Month/Year math is still unverified.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Personal Day for today. Write one scheduling suggestion (e.g. “batch focus,” “reach out,” “rest”) — one line only.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**How to interpret timing periods** shows how Year, Month, Day, cycles, and pinnacles stack without contradiction.',
        ],
      },
    ],
    howInReport: 'Day emphasis stays light — context, not command.',
    ctaNote,
  },

  'm4-life-cycles': {
    title: 'Life Cycles',
    section: 'forecasts',
    seo: ['Life cycles numerology'],
    intro:
      'Life Cycles divide the life into **three long chapters**, each with its own dominant digit. They describe **when** certain themes take the lead — not who you are forever.',
    blocks: [
      {
        h2: 'Building blocks from the birth date',
        p: [
          'Reduce the birth month to one digit (or 11 for November if your system keeps it for this step). Reduce the birth day to one digit (11 or 22 as day may stay as master subtotals — follow your LP rules). Reduce the birth year to one digit or master.',
          'Cycle 1 digit = month + day (then reduce). Cycle 2 digit = day + year (then reduce). Cycle 3 digit = Cycle 1 digit + Cycle 2 digit (then reduce). These three digits are the cycle **themes**.',
        ],
      },
      {
        h2: 'Durations (common Pythagorean approach)',
        p: [
          'Let LP be the Life Path reduced to a single digit 1–9 (if LP is 11, 22, or 33, many teachers reduce to 2, 4, or 6 for this duration formula).',
          'First cycle length ≈ 36 − LP. Second cycle length ≈ 36. Third cycle runs from the end of the second to end of life.',
          'Exact start ages vary by school; use one method consistently in a given reading.',
        ],
      },
      {
        h2: 'How to read them',
        p: [
          'Cycle 1: formative tone — what you are learning to become. Cycle 2: productive tone — what you build and prove. Cycle 3: integrative tone — what you distill and pass on.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          'Birth 14 July 1986 → month 7, day 14 → 5, year 1986 → 6. Cycle1 = 7+5 = 12 → 3. Cycle2 = 5+6 = 11 → 2 or 11 per rules. Cycle3 = 3 + (2 or 11) → reduce accordingly. Interpret each digit with the 1–9 vocabulary.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Stack Life Cycles **under** Life Path: long arc first, then which chapter you are in.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Using the worked example date (14 July 1986), recompute Cycle 1–3 digits on paper and note your age when Cycle 1 ends if you use the 36−LP formula.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Pinnacles** and **Challenges** add peak themes and friction lines across life quarters.',
        ],
      },
    ],
    howInReport: 'When cycle boundaries are available, they frame life chapters alongside Personal Year.',
    ctaNote: ctaCourse,
  },

  'm4-pinnacles': {
    title: 'Pinnacles',
    section: 'forecasts',
    seo: ['Pinnacles numerology'],
    intro:
      'Pinnacles are **four challenge–opportunity peaks** derived from the birth date. They name what each life quarter asks you to grow — not a score.',
    blocks: [
      {
        h2: 'Calculation (standard set)',
        p: [
          'Let M, D, Y be month, day, and birth year each fully reduced to a single digit (or 11/22 where your rules keep masters for sub-steps).',
          'Pinnacle 1 = M + D (reduce). Pinnacle 2 = D + Y (reduce). Pinnacle 3 = M + Y (reduce). Pinnacle 4 = P1 + P2 + P3 (reduce).',
        ],
      },
      {
        h2: 'Timing the pinnacles',
        p: [
          'Durations often mirror Life Cycle lengths: first pinnacle dominates the first long chapter, second the second chapter, third the third, fourth colours the closing integrative phase. Align pinnacle index with the cycle you are in when you read.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Each pinnacle digit uses the same 1–9 language: Pinnacle 4 years reward structure; Pinnacle 5 years reward honest change. Name the growth edge, not doom.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use Pinnacles to answer “what is this chapter trying to teach?” alongside Personal Year’s seasonal weather.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute P1–P4 for one birth date. Write one growth sentence for the pinnacle digit matching your current age bracket.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Challenges** name friction between date components — pair with pinnacles, not instead of them.',
        ],
      },
    ],
    howInReport: 'Optional when pinnacle tables are generated.',
    ctaNote: ctaCourse,
  },

  'm4-challenges': {
    title: 'Challenges',
    section: 'forecasts',
    seo: ['Numerology challenges'],
    intro:
      'Challenge numbers measure **friction between parts of the birth date** — inner homework, not punishment. They pair naturally with Pinnacles: pinnacles name opportunity; challenges name resistance.',
    blocks: [
      {
        h2: 'Calculation (common set)',
        p: [
          'Reduce month, day, and year each to a single digit (1–9).',
          'Challenge 1 = |month − day|. Challenge 2 = |day − year|. Challenge 3 = |Challenge1 − Challenge2|. Main Challenge = |month − year|.',
          'If a challenge is two digits, reduce to one unless your system keeps 11/22 as master challenges.',
        ],
      },
      {
        h2: 'How to language them',
        p: [
          'A 3 challenge might be discomfort with visibility; an 8 challenge might be discomfort with power or money. Describe behaviour and growth, not character flaws.',
        ],
      },
      {
        h2: 'Relation to pinnacles',
        p: [
          'When a pinnacle asks for structure (4) and a challenge highlights restlessness (5), the reading names the negotiation: build containers without killing curiosity.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'One clear sentence per challenge in a client reading is enough — then return to core synthesis.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute Challenge 1–3 and Main Challenge for one date. For each, one behavioural sentence — no shame words.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**How to interpret timing periods** integrates Year/Month/Day with cycles and pinnacles.',
        ],
      },
    ],
    howInReport: 'Optional when challenge numbers are computed.',
    ctaNote: ctaCourse,
  },

  'm4-interpret-timing': {
    title: 'How to interpret timing periods',
    section: 'forecasts',
    seo: ['Interpret numerology timing'],
    intro:
      'You now have **several timing tools**: Personal Year / Month / Day, Life Cycles, Pinnacles, and Challenges. This lesson is the **interpretation layer**: how they talk to each other without contradicting your core chart.',
    blocks: [
      {
        h2: 'Stack order (what sits on what)',
        p: [
          '1) **Life Path + core name numbers** = terrain. 2) **Life Cycle** = which long chapter. 3) **Pinnacle / Challenge** for that chapter = lesson and friction. 4) **Personal Year** = weather this year. 5) **Month / Day** = fine mist — not the whole story.',
        ],
      },
      {
        h2: 'When systems disagree',
        p: [
          'A heavy Personal Year 8 does not erase a reflective Life Path 7 — it asks how power and visibility show up **for a person who prefers depth**. That tension is the reading.',
        ],
      },
      {
        h2: 'Contradictions are data',
        p: [
          'If Cycle theme is 3 (expression) and Challenge is 4 (structure), the person may feel pulled between visibility and patience. Name the trade-off; do not pick a winner.',
        ],
      },
      {
        h2: 'Practical pacing',
        p: [
          'Use Year for plans, Month for projects, Day for tasks. Reserve Day energy for nuance, not for rewriting identity.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Lead with terrain, season with cycle/pinnacle/challenge, tint with Personal Year — never the reverse.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Write a five-sentence mini reading for yourself: (1) Life Path, (2) Personal Year, (3) one cycle or pinnacle if you use them, (4) tension between two layers, (5) one pacing suggestion.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Module 6 **life cycles** lessons deepen long arcs; revisit **samples** when you want to see full report structure.',
        ],
      },
    ],
    howInReport: 'Timing layers are woven as context around core synthesis.',
    ctaNote,
  },

  'm5-life-path-expression': {
    title: 'Life Path + Expression',
    section: 'chart',
    seo: ['Life path and expression'],
    intro:
      'Life Path is the long arc; Expression is how you tend to operate. When they agree, the story feels coherent. When they diverge, you get a productive tension between chapter and instrument.',
    blocks: [
      {
        h2: 'How to read the pair',
        p: [
          'Ask: does the Expression digit give the Life Path a clear stage, or does it rush a slow story? Does the Life Path ask for patience while Expression wants visibility?',
        ],
      },
    ],
    howInReport: 'Pairs are written as negotiation, not contradiction.',
    ctaNote,
  },

  'm5-soul-urge-vs-personality': {
    title: 'Soul Urge vs Personality',
    section: 'chart',
    seo: ['Soul urge personality'],
    intro:
      'Soul Urge is inner appetite; Personality is outer first pass. Alignment feels effortless; divergence names privacy, performance, and the cost of bridging both.',
    blocks: [
      {
        h2: 'Reading the split',
        p: [
          'Use the split as context for work and intimacy: what you crave versus what you show first — skill-building, not moral failure.',
        ],
      },
    ],
    howInReport: 'Name synthesis often rests here.',
    ctaNote,
  },

  'm5-repeating-numbers': {
    title: 'Repeating numbers',
    section: 'chart',
    seo: ['Repeating numbers'],
    intro:
      'When a digit appears in multiple positions, the theme amplifies — but the placement still matters. Repetition is emphasis, not automatic mastery.',
    blocks: [
      {
        h2: 'How to weigh repetition',
        p: [
          'Trace each occurrence to its position before you declare a “dominant” digit. Two 5s in different offices do not mean the same thing twice.',
        ],
      },
    ],
    howInReport: 'Amplification appears in synthesis when the chart supports it.',
    ctaNote,
  },

  'm5-contradictions': {
    title: 'Contradicting numbers',
    section: 'chart',
    seo: ['Contradictions in chart'],
    intro:
      'Contrasts are normal. A 4 Life Path beside a 3 Expression is not hypocrisy — it is two legitimate languages that need sequencing and boundaries.',
    blocks: [
      {
        h2: 'Integration',
        p: [
          'Name the tension in plain language, then ask which need leads in which season. The chart reading lesson expands this skill.',
        ],
      },
    ],
    howInReport: 'Contrasts are written as integration work.',
    ctaNote,
  },

  'm5-dominant-patterns': {
    title: 'Dominant patterns',
    section: 'chart',
    seo: ['Dominant patterns numerology'],
    intro:
      'Look for clusters: several positions landing on the same digit family, or one digit answering multiple life questions. Dominance is a reading choice — justify it from the chart.',
    blocks: [
      {
        h2: 'Method',
        p: [
          'List positions and digits, then step back for pattern before you narrate. Dominant lines reduce noise; they should not erase nuance.',
        ],
      },
    ],
    howInReport: 'Dominant themes appear when the data supports them.',
    ctaNote,
  },

  'm5-missing-energies': {
    title: 'Missing energies',
    section: 'chart',
    seo: ['Missing numbers'],
    intro:
      'Digits absent from the name (or underrepresented) point to skills built through practice rather than instinct — parallel to karmic-lesson language, but lighter.',
    blocks: [
      {
        h2: 'How to language absence',
        p: [
          'Avoid deficiency framing. Missing is “asks for attention,” not “broken.” Pair with the 1–9 vocabulary for constructive wording.',
        ],
      },
    ],
    howInReport: 'Optional emphasis when gaps are clear.',
    ctaNote,
  },

  'm6-example-reading-1': {
    title: 'Example reading 1 — Edgar Allan Poe (study sample)',
    section: 'readings',
    seo: ['Example numerology reading'],
    intro:
      'This example uses historical birth data as study material — not a biographical verdict. Open the structured report view to see how sections connect.',
    blocks: [
      {
        h2: 'What to study',
        p: [
          'Scroll the report in order: summary → core numbers → themes → life areas → timing. After each section, pause and ask: “What question did this section answer?”',
        ],
      },
      {
        h2: 'Skills to practise',
        p: [
          'Quote one number, then quote a second that modifies it — e.g. how a Life Path theme meets an Expression style. Avoid treating each number as a separate essay.',
        ],
      },
      {
        h2: 'Next step',
        p: [
          'Open Example 2 and compare: same layout, different digits — notice how structure stays constant while content changes.',
        ],
      },
    ],
    howInReport: 'Use the sample link from the study samples page.',
    ctaNote: ctaCourse,
  },

  'm6-example-reading-2': {
    title: 'Example reading 2 — Marie Curie (study sample)',
    section: 'readings',
    seo: ['Example numerology reading'],
    intro:
      'Second study sample with the same report architecture: use it to compare structure side by side with Example 1.',
    blocks: [
      {
        h2: 'How to use it',
        p: [
          'Read Curie after Poe with a checklist: Where does synthesis appear? Where does timing sit relative to core numbers? How long is each section?',
        ],
      },
      {
        h2: 'Comparison drill',
        p: [
          'Pick one digit that appears in both charts (if any) and note how **placement** changes the meaning — same vocabulary, different job.',
        ],
      },
      {
        h2: 'Next step',
        p: [
          'Move to Example 3 for a composite, anonymised walk-through without a celebrity frame.',
        ],
      },
    ],
    howInReport: 'Samples mirror the personal report structure.',
    ctaNote: ctaCourse,
  },

  'm6-example-reading-3': {
    title: 'Example reading 3 — Composite chart (teaching walk-through)',
    section: 'readings',
    seo: ['Example numerology reading'],
    intro:
      'This is a **fictional, anonymised composite** for teaching: **Jordan Lee**, born **18 May 1992**, full birth name **JORDAN ALEX LEE**. It is not a real client file. Use it to practise the same moves you used on Poe and Curie — without biography noise.',
    blocks: [
      {
        h2: 'Step 1 — What you would compute first',
        p: [
          'Life Path from 18/05/1992; Expression, Soul Urge, Personality from the full name; Birthday from 18; Personal Year for the calendar year you choose to study.',
        ],
      },
      {
        h2: 'Step 2 — Narrative spine (sample)',
        p: [
          'For 18 May 1992, compute Life Path first (here: **8**). Then say: “Long arc emphasises stewardship and consequence; the name cluster adds [Expression (Destiny) / Soul Urge / Personality]; birthday adds close-up texture; the current Personal Year colours this season with [theme].” Every clause should tie to a position.',
        ],
      },
      {
        h2: 'Step 3 — Honest tension',
        p: [
          'Name one contrast: e.g. if Soul Urge pulls toward privacy and Expression rewards visibility, the reading’s job is to schedule those needs — not to pick one.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Composite charts let you rehearse structure without ethical weight. Run the same numbers in your calculator or engine to verify your arithmetic.',
        ],
      },
    ],
    howInReport: 'Parallel to how a full report would read — numbers are illustrative only.',
    ctaNote: ctaCourse,
  },

  'm6-step-by-step-breakdown': {
    title: 'Step-by-step reading breakdown',
    section: 'readings',
    seo: ['Numerology reading steps'],
    intro:
      'Use this as a **repeatable checklist** every time you read a chart — from study samples to your own work. One layer at a time; synthesis last.',
    blocks: [
      {
        h2: 'Layer 1 — Verify data',
        p: [
          'Confirm birth date, spelling of birth name, and which year you are using for Personal Year. Most “wrong readings” are data errors.',
        ],
      },
      {
        h2: 'Layer 2 — Life Path',
        p: [
          'State the long arc in one paragraph: what kind of chapters repeat, what pace of growth feels native. No advice yet.',
        ],
      },
      {
        h2: 'Layer 3 — Name cluster',
        p: [
          'Expression, Soul Urge, Personality — one paragraph that treats them as one conversation. End with one sentence on how they agree or diverge.',
        ],
      },
      {
        h2: 'Layer 4 — Birthday + secondary',
        p: [
          'Birthday texture. Then add maturity, karmic markers, or balance **only if** you have calculated them and they add clarity.',
        ],
      },
      {
        h2: 'Layer 5 — Timing',
        p: [
          'Personal Year first, then Month if relevant, then Day for light pacing. Tie to core: “This season asks X of someone whose terrain is Y.”',
        ],
      },
      {
        h2: 'Layer 6 — Synthesis',
        p: [
          'Two short paragraphs: (1) dominant themes and tensions; (2) one reflective question — what this person is learning **now**, not forever.',
        ],
      },
    ],
    howInReport: 'Professional reports follow a similar layer order.',
    ctaNote,
  },

  'm6-read-profile-start-to-finish': {
    title: 'How to read a profile from start to finish',
    section: 'readings',
    seo: ['Read numerology profile'],
    intro:
      'A profile is coherent when **each section earns the next**. This lesson is the **reader’s flow**: what to open with, what to hold back, and how to close.',
    blocks: [
      {
        h2: 'Opening',
        p: [
          'Scope and method in two sentences: what you are looking at (birth name and date) and that numbers describe themes, not fate.',
        ],
      },
      {
        h2: 'Middle',
        p: [
          'Core first, timing second, secondary numbers where useful. If you feel stuck, return to Life Path + Expression before adding more layers.',
        ],
      },
      {
        h2: 'Closing',
        p: [
          'Strengths, frictions, and **one** season-aware reflection — not a list of adjectives. The reader should leave knowing what to pay attention to, not overwhelmed.',
        ],
      },
      {
        h2: 'What to avoid',
        p: [
          'Dumping every number with equal weight. Leading with Personal Year before terrain. Using “karmic” language to alarm.',
        ],
      },
    ],
    howInReport: 'Mirrors disciplined report flow.',
    ctaNote,
  },

  'm7-full-reading-framework': {
    title: 'Full reading framework',
    section: 'readings',
    seo: ['Full numerology reading'],
    intro:
      'This is the **capstone framework** for delivering a complete reading: gather → core → texture → timing → synthesis → close. Use it as a mental outline or a literal section list.',
    blocks: [
      {
        h2: '1. Intake',
        p: [
          'Confirm birth certificate name, birth date, and current year for timing. Note the person’s question (if any) — timing answers land better when you know the season of life.',
        ],
      },
      {
        h2: '2. Core portrait',
        p: [
          'Life Path paragraph. Name cluster paragraph. Birthday paragraph. Each paragraph cites positions.',
        ],
      },
      {
        h2: '3. Depth and friction',
        p: [
          'Secondary numbers (Module 4) only where they sharpen the story. Contrasts between positions — one paragraph.',
        ],
      },
      {
        h2: '4. Timing stack',
        p: [
          'Life Cycle / Pinnacle / Challenge if used; Personal Year + optional Month/ Day. One paragraph that ties weather to terrain.',
        ],
      },
      {
        h2: '5. Integration',
        p: [
          'Answer: what are the three threads that repeat? What is one behaviour to watch? What is one compassionate next step?',
        ],
      },
      {
        h2: '6. Close',
        p: [
          'Reflective prompt — not a command. Leave the person with agency.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Outline a real or fictional chart using the six steps as headings only — one honest question per heading you cannot answer yet. That gap list is your study agenda.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Final full reading example** shows tone and length; study **samples** for live report layout.',
        ],
      },
    ],
    howInReport: 'Pedagogical parallel to full report sections; optional report product applies this to your data.',
    ctaNote: ctaCourse,
  },

  'm7-how-to-write-interpretations': {
    title: 'How to write interpretations',
    section: 'readings',
    seo: ['Write numerology interpretations'],
    intro:
      'Good interpretation reads like **clear nonfiction**: grounded verbs, named positions, and honest uncertainty. This lesson is the style guide for everything you write.',
    blocks: [
      {
        h2: 'Sentence-level habits',
        p: [
          'Prefer “tends to,” “often,” “asks for,” over “you are.” Name at least two chart relationships in every paragraph.',
        ],
      },
      {
        h2: 'Cite the chart',
        p: [
          'Example: “With a 6 Life Path, themes of care and responsibility return; your Expression 3 adds visibility and language — so the work may be speaking for others without burning out.”',
        ],
      },
      {
        h2: 'Tone',
        p: [
          'Warm, direct, no fortune-telling. Avoid “the universe” filler. Avoid medical or legal certainty.',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'If a sentence could apply to anyone, it is not anchored in the chart yet.',
        ],
      },
    ],
    howInReport: 'Report copy follows these habits.',
    ctaNote: ctaCourse,
  },

  'm7-combine-major-numbers': {
    title: 'How to combine all major numbers',
    section: 'readings',
    seo: ['Combine numerology numbers'],
    intro:
      'Synthesis is **selective**: choose dominant lines, then show how secondary numbers modify them. One paragraph of integration beats five paragraphs of labels.',
    blocks: [
      {
        h2: 'Merge rules',
        p: [
          'Start from Life Path + Expression; fold in Soul Urge and Personality; let Birthday refine; let timing season the conclusion.',
        ],
      },
      {
        h2: 'The integration paragraph',
        p: [
          'Template: “The long arc emphasises [LP]. The name suggests [E/SU/P]. Together, that reads as [tension or harmony]. This year’s [PY] colours that story by [theme].”',
        ],
      },
      {
        h2: 'What not to do',
        p: [
          'Listing every digit with equal weight. Hiding contradictions. Resolving tension with a single number (“you are really a 5”).',
        ],
      },
    ],
    howInReport: 'Demonstrated in the optional report product.',
    ctaNote: ctaCourse,
  },

  'm7-structure-complete-reading': {
    title: 'How to structure a complete reading',
    section: 'readings',
    seo: ['Structure numerology reading'],
    intro:
      'Open with scope and method. Body: core positions, then secondary layers if used, then timing. Close with strengths, frictions, and one season-aware reflection prompt.',
    blocks: [
      {
        h2: 'Sections',
        p: [
          'Keep sections visible so the reader can verify your logic. Avoid burying calculation in prose.',
        ],
      },
      {
        h2: 'Suggested headings',
        p: [
          'Introduction · Life Path · Name numbers · Birth day · (Optional) Secondary numbers · Timing · Synthesis · Reflective close.',
        ],
      },
      {
        h2: 'Length',
        p: [
          'Depth beats verbosity. A strong reading can be shorter than a weak one if every sentence earns its place.',
        ],
      },
    ],
    howInReport: 'Parallel to report sectioning.',
    ctaNote: ctaCourse,
  },

  'm7-final-example': {
    title: 'Final full reading example',
    section: 'readings',
    seo: ['Numerology reading example'],
    intro:
      'Below is a **short, complete** illustrative reading for the same fictional composite as Example 3 (**Jordan Lee**, 18 May 1992, **JORDAN ALEX LEE**). Numbers below are **for demonstration** — always verify with your own calculation.',
    blocks: [
      {
        h2: 'Illustrative numbers (always verify)',
        p: [
          'For **18 May 1992**, month 5 + day 18 (9) + year 1992 (3) → 5 + 9 + 3 = 17 → **8** Life Path. The name totals below are **illustrative teaching labels** — recompute Expression, Soul Urge, and Personality yourself.',
        ],
      },
      {
        h2: 'Opening',
        p: [
          '“This chart looks at the birth name JORDAN ALEX LEE and birth date 18 May 1992. Numbers describe themes and pressures — not a fixed identity.”',
        ],
      },
      {
        h2: 'Core synthesis (sample)',
        p: [
          '“An 8 Life Path often returns to impact, resources, and responsibility under pressure — learning integrity in power rather than control for its own sake. If Expression reads as 3, communication and visibility become part of how influence shows up: the story may ask how stewardship and voice share the same stage. If Soul Urge reads as 1, there is a private appetite for self-authorship; if Personality reads as 5, first contact may feel mobile and curious before depth appears. Together that can feel like: lead with clarity, but don’t mistake motion for meaning.”',
        ],
      },
      {
        h2: 'Timing (sample)',
        p: [
          '“For any calendar year you choose, Personal Year would colour whether this is a year of consolidation, visibility, or change — name the year and tie it to the 8’s material and ethical stakes and the 3’s need to speak.”',
        ],
      },
      {
        h2: 'Close',
        p: [
          '“Reflective question: Where are you building something that will outlast you — and what would honest pacing change this season?”',
        ],
      },
      {
        h2: 'Takeaway',
        p: [
          'Use this as a **pattern** for length, tone, and integration — then substitute verified numbers for any real chart.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Rewrite the sample synthesis paragraph using **your** verified core numbers only — same structure, your digits, no new positions.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Return to **course home** to pick up any lesson you skipped; use **sample readings** to study report architecture.',
        ],
      },
    ],
    howInReport: 'Illustrative only; optional report generates your verified chart.',
    ctaNote: ctaCourse,
  },

  'appendix-quick-reference': {
    title: 'Quick reference guide',
    section: 'about',
    seo: ['Numerology quick reference'],
    intro:
      'A compact refresher for this course’s method: letter values, reduction, where each number lives in a chart, timing layers, and short digit handles. Use while you practise or skim a report — not a substitute for the full lessons, which carry nuance, examples, and integration.',
    blocks: [
      {
        h2: 'What this page is for',
        p: [
          'You already learned the ideas in the modules; this is the same rules on one screen. When a digit appears, placement still wins: Life Path is not Expression, and timing does not replace core numbers.',
        ],
      },
      {
        h2: 'Pythagorean letter values (A–Z)',
        p: [
          'Letters map in a repeating 1–9 cycle: A–I → 1–9, J–R → 1–9, S–Z → 1–8 (Z = 8). Y is handled as vowel or consonant by the rules in the name-number lessons (including Soul Urge).',
        ],
        ul: [
          'A J S → 1',
          'B K T → 2',
          'C L U → 3',
          'D M V → 4',
          'E N W → 5',
          'F O X → 6',
          'G P Y → 7',
          'H Q Z → 8',
          'I R → 9',
        ],
      },
      {
        h2: 'Reduction',
        p: [
          'Add digits until you reach a single digit 1–9, unless the rule for that **position** allows stopping at 11, 22, or 33 on the **final** step. Intermediate 11s in scratch work are not automatically “your” master number.',
        ],
      },
      {
        h2: 'Core positions (where numbers sit)',
        ul: [
          'Life Path — full birth date; long arc.',
          'Expression (Destiny) — full birth name; how you tend to operate outwardly.',
          'Soul Urge — vowels of birth name; inner drive.',
          'Personality — consonants of birth name; first impression / outer style.',
          'Birthday — day of month; gift tone.',
          'Maturity — Life Path + Expression, read as later-life integration (see lesson).',
          'Secondary layers (maturity aside) — attitude, balance, hidden passion, karmic markers: optional depth after core is clear.',
        ],
      },
      {
        h2: 'Timing stack',
        p: [
          'Personal Year — dominant theme for the calendar year (from birth month/day + year).',
          'Personal Month — finer layer inside the year.',
          'Personal Day — light daily nuance; not a rewrite of identity.',
          'Life cycles, pinnacles, challenges — longer arcs (separate lessons).',
        ],
      },
      {
        h2: 'Digits 1–9 — concise handles',
        p: [
          'Same vocabulary as the Numbers 1–9 lesson, shortened. Shadow, overuse, and placement nuance stay in that lesson and in integrated readings.',
        ],
        ul: [
          '1 — Initiative; standing in your own start.',
          '2 — Cooperation; sensitivity; real harmony under pressure.',
          '3 — Expression; shape and share what matters.',
          '4 — Structure; build what lasts.',
          '5 — Change; movement; honest variety.',
          '6 — Care; responsibility; belonging.',
          '7 — Depth; study; discernment.',
          '8 — Power; impact; resources with integrity.',
          '9 — Completion; perspective; release.',
        ],
      },
      {
        h2: 'Master numbers 11, 22, 33',
        p: [
          'Only when the **final reduction for that position** preserves 11, 22, or 33 per your engine’s rules. Themes widen — more voltage, more integration work — while the related single digit (2, 4, or 6) still matters as a ground floor.',
        ],
      },
      {
        h2: 'Using this alongside the course',
        p: [
          'Keep **Numbers 1–9** as your meaning home base. Return here when you need the letter cycle or a position list without rereading a whole module. Generated reports apply these same rules to your data.',
        ],
      },
    ],
    howInReport: 'Optional cross-check when reading generated output — same reductions and positions as the lessons.',
    ctaNote: ctaCourse,
  },

  'appendix-number-meanings-cheat-sheet': {
    title: 'Number meanings cheat sheet',
    section: 'about',
    seo: ['Numerology cheat sheet'],
    intro:
      'This page is not linked from the public course appendix. Use the **Quick reference guide** (digit handles + method) and the **Numbers 1–9** lesson for full depth.',
    blocks: [
      {
        h2: 'Redirect',
        p: [
          'For a production-quality summary, open **Quick reference guide** in the appendix, or the main lesson **Numbers 1–9** for shadow edges and placement.',
        ],
      },
    ],
    howInReport: 'Vocabulary check only — placement still governs.',
    ctaNote: ctaCourse,
  },
};
